import { useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { list, create, update, remove, checkConflicts } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { ExportButtons } from "@/components/ExportButtons";
import { Field, Section, RefSelect } from "@/components/FormField";
import { RfqPanel } from "@/components/RfqPanel";
import { CostEstimator } from "@/components/CostEstimator";
import { FreightClassField } from "@/components/FreightClassField";
import { StatusPill, PriorityPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Pencil, Trash2, Search, AlertTriangle, ArrowRight, Upload, FileCheck } from "lucide-react";
import { toast } from "sonner";
import {
  PRIORITY, ASSET_SOURCE, STATUS, CONTRACT_TYPE,
} from "@/constants/options";

const fmtDate = (v) => (v ? String(v).slice(0, 10) : "—");
const money = (v) => (v === null || v === undefined || v === "" ? "—" : `$${Number(v).toLocaleString()}`);

const STATUS_FLOW = ["Draft", "Submitted", "Approved", "Scheduled", "In Progress", "Completed"];
const hasVal = (v) => v !== null && v !== undefined && v !== "";

// Fields that must be complete before a requisition can move into the given next stage.
function requirementsFor(next, r) {
  const isInternal = (r.asset_source || "Internal Asset") === "Internal Asset";
  const miss = [];
  const need = (cond, label) => { if (!cond) miss.push(label); };
  if (next === "Submitted") {
    need(hasVal(r.requester_name), "Requester Name");
    need(hasVal(r.project_name), "Project Name");
    need(hasVal(r.pickup_location), "Pickup Location");
    need(hasVal(r.delivery_location), "Delivery Location");
    need(hasVal(r.load_description), "Load Description");
  } else if (next === "Approved") {
    need(hasVal(r.approval_document), "Approval Document");
    need(hasVal(r.approved_by), "Approved By");
  } else if (next === "Scheduled") {
    need(hasVal(r.estimated_start), "Estimated Start");
    need(hasVal(r.estimated_end), "Estimated End");
    if (isInternal) { need(hasVal(r.driver_id), "Driver"); need(hasVal(r.truck_id), "Truck"); }
    else { need(hasVal(r.third_party_carrier), "Carrier/Vendor"); }
  } else if (next === "In Progress") {
    need(hasVal(r.estimated_cost), "Estimated Cost");
  } else if (next === "Completed") {
    need(hasVal(r.actual_cost), "Actual Cost");
  }
  return miss;
}

export default function MasterLog() {
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["requisitions"], queryFn: () => list("requisitions") });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: () => list("drivers") });
  const { data: assets = [] } = useQuery({ queryKey: ["assets"], queryFn: () => list("assets") });
  const { data: vendors = [] } = useQuery({ queryKey: ["vendors"], queryFn: () => list("vendors") });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [q, setQ] = useState("");
  const [delId, setDelId] = useState(null);
  const [conflicts, setConflicts] = useState(null);
  const [checking, setChecking] = useState(false);
  const [sp, setSp] = useSearchParams();
  const fStatus = sp.get("status"); const fSource = sp.get("source"); const fPriority = sp.get("priority");
  const fActive = sp.get("active"); const fDeadleg = sp.get("deadleg");
  const filterLabel = fStatus || fPriority || fSource || (fActive && "Active jobs") || (fDeadleg && "Jobs with dead-leg");
  const approvalFileRef = useRef(null);
  const onApprovalFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, approval_document: reader.result, approval_document_name: file.name }));
    reader.readAsDataURL(file);
  };

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["requisitions"] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
    qc.invalidateQueries({ queryKey: ["trips"] });
  };
  const saveMut = useMutation({
    mutationFn: (body) => (editing ? update("requisitions", editing.id, body) : create("requisitions", body)),
    onSuccess: () => { invalidate(); setOpen(false); toast.success(editing ? "Requisition updated" : "Requisition created"); },
    onError: () => toast.error("Save failed"),
  });
  const delMut = useMutation({
    mutationFn: (id) => remove("requisitions", id),
    onSuccess: () => { invalidate(); setDelId(null); toast.success("Deleted"); },
  });
  const advanceMut = useMutation({
    mutationFn: ({ id, body }) => update("requisitions", id, body),
    onSuccess: (_d, v) => { invalidate(); toast.success(`Advanced to ${v.body.status}`); },
    onError: () => toast.error("Could not advance status"),
  });

  const openNew = () => { setEditing(null); setForm({ priority: "Normal", asset_source: "Internal Asset", status: "Draft" }); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm(row); setOpen(true); };
  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const handleSave = async () => {
    setChecking(true);
    try {
      const { conflicts: c } = await checkConflicts({ ...form, id: editing?.id });
      if (c && c.length) { setConflicts(c); return; }
    } catch (e) { console.error("Conflict check failed, proceeding (fail open):", e); }
    finally { setChecking(false); }
    saveMut.mutate(form);
  };

  const advance = (r) => {
    const idx = STATUS_FLOW.indexOf(r.status);
    if (idx < 0 || idx >= STATUS_FLOW.length - 1) return;
    const next = STATUS_FLOW[idx + 1];
    const missing = requirementsFor(next, r);
    if (missing.length) {
      toast.error(`Complete before "${next}": ${missing.join(", ")}`);
      openEdit(r);
      return;
    }
    const body = { status: next };
    if (next === "In Progress" && !r.actual_start) body.actual_start = new Date().toISOString();
    if (next === "Completed" && !r.closed_date) body.closed_date = new Date().toISOString().slice(0, 10);
    advanceMut.mutate({ id: r.id, body });
  };

  const filtered = data.filter((r) => {
    if (q && !JSON.stringify(r).toLowerCase().includes(q.toLowerCase())) return false;
    if (fStatus && r.status !== fStatus) return false;
    if (fSource && (r.asset_source || "Internal Asset") !== fSource) return false;
    if (fPriority && r.priority !== fPriority) return false;
    if (fActive && !["Submitted", "Approved", "Scheduled", "In Progress"].includes(r.status)) return false;
    if (fDeadleg && !(r.dead_leg_cost || r.dead_leg_miles)) return false;
    return true;
  });
  const isInternal = (form.asset_source || "Internal Asset") === "Internal Asset";
  const trucks = assets.filter((a) => a.category === "Truck");
  const trailers = assets.filter((a) => a.category === "Trailer");
  const selectDriver = (id) => { const d = drivers.find((x) => x.id === id); setForm((p) => ({ ...p, driver_id: id, driver_name: d?.driver_name, employee_id: d?.employee_id })); };
  const selectTruck = (id) => { const a = trucks.find((x) => x.id === id); setForm((p) => ({ ...p, truck_id: id, truck_unit_number: a?.unit_number, truck_type: a?.asset_type, eia_region: p.eia_region || a?.eia_region })); };
  const selectTrailer = (id) => { const a = trailers.find((x) => x.id === id); setForm((p) => ({ ...p, trailer_id: id, trailer_number: a?.unit_number, trailer_type: a?.asset_type })); };

  return (
    <Layout>
      <PageHeader title="Master Log" subtitle="Central logistics requisition & work order record">
        <ExportButtons collection="requisitions" />
        <Button size="sm" data-testid="add-requisition-btn" onClick={openNew} className="gap-2 bg-[#0055FF] hover:bg-[#0044CC]">
          <Plus className="h-4 w-4" /> New Requisition
        </Button>
      </PageHeader>

      <div className="p-8 space-y-4 animate-fade-up">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="search-requisitions" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search requisitions…" className="pl-9 h-9" />
        </div>

        {filterLabel && (
          <div data-testid="active-filter-banner" className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#0055FF]/10 text-[#0055FF] px-3 py-1 font-medium">
              Filtered: {filterLabel}
              <button data-testid="clear-filter-btn" onClick={() => setSp({})} className="hover:text-[#0044CC]" title="Clear filter">✕</button>
            </span>
            <span className="text-muted-foreground">{filtered.length} match{filtered.length === 1 ? "" : "es"}</span>
          </div>
        )}
        <div className="w-full overflow-auto rounded-md border bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                {["REQ #", "WO #", "Request Date", "Project", "Pickup → Delivery", "Priority", "Source", "Status", "Approval", "Assigned", "Est Cost", "Dead Leg $"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-[0.08em] font-semibold whitespace-nowrap">{h}</TableHead>
                ))}
                <TableHead className="text-right w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={13} className="text-center py-10 text-muted-foreground">No requisitions yet.</TableCell></TableRow>
              ) : filtered.map((r) => {
                const idx = STATUS_FLOW.indexOf(r.status);
                const next = idx >= 0 && idx < STATUS_FLOW.length - 1 ? STATUS_FLOW[idx + 1] : null;
                const missing = next ? requirementsFor(next, r) : [];
                const ready = next && missing.length === 0;
                return (
                <TableRow key={r.id} data-testid={`req-row-${r.id}`} className="hover:bg-muted/50 cursor-pointer" onClick={() => openEdit(r)}>
                  <TableCell className="font-mono-data text-[13px] font-semibold text-[#0055FF]">{r.requisition_no}</TableCell>
                  <TableCell className="font-mono-data text-[13px]">{r.work_order_no || <span className="text-neutral-300">—</span>}</TableCell>
                  <TableCell className="font-mono-data text-[13px] whitespace-nowrap">{fmtDate(r.request_date)}</TableCell>
                  <TableCell className="text-[13px] whitespace-nowrap max-w-[160px] truncate">{r.project_name || "—"}</TableCell>
                  <TableCell className="text-[13px] whitespace-nowrap">{(r.pickup_location || "?")} → {(r.delivery_location || "?")}</TableCell>
                  <TableCell><PriorityPill value={r.priority} /></TableCell>
                  <TableCell className="text-[12px] whitespace-nowrap">{r.asset_source}</TableCell>
                  <TableCell><StatusPill value={r.status} /></TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {r.approval_document
                      ? <a href={r.approval_document} target="_blank" rel="noopener noreferrer" download={r.approval_document_name || "approval"} data-testid={`approval-doc-${r.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 px-2.5 py-0.5 text-xs font-semibold"><FileCheck className="h-3 w-3" /> Approved</a>
                      : <span className="text-neutral-300">—</span>}
                  </TableCell>
                  <TableCell className="text-[12px] whitespace-nowrap">{r.driver_name || r.third_party_carrier || <span className="text-neutral-300">—</span>}</TableCell>
                  <TableCell className="font-mono-data text-[13px] text-right tabular-nums">{money(r.estimated_cost)}</TableCell>
                  <TableCell className="font-mono-data text-[13px] text-right tabular-nums" data-testid={`dead-leg-cost-${r.id}`}>{money(r.dead_leg_cost)}</TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {next && (
                        <Button variant="ghost" size="icon"
                          className={`h-8 w-8 ${ready ? "text-[#0055FF] hover:bg-[#0055FF]/10" : "text-neutral-300"}`}
                          data-testid={`advance-req-${r.id}`}
                          title={ready ? `Advance to "${next}"` : `To reach "${next}", complete: ${missing.join(", ")}`}
                          disabled={advanceMut.isPending}
                          onClick={() => advance(r)}>
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-req-${r.id}`} onClick={() => openEdit(r)}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" data-testid={`delete-req-${r.id}`} onClick={() => setDelId(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {editing ? `${editing.requisition_no}` : "New Requisition"}
              {editing?.work_order_no && <span className="text-sm font-mono-data text-muted-foreground">/ {editing.work_order_no}</span>}
            </DialogTitle>
            <DialogDescription>Manage requisition details across request, schedule, assignment and cost stages.</DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="request" className="w-full">
            <TabsList className="grid grid-cols-4 w-full">
              <TabsTrigger value="request" data-testid="tab-request">Request</TabsTrigger>
              <TabsTrigger value="schedule" data-testid="tab-schedule">Schedule</TabsTrigger>
              <TabsTrigger value="assign" data-testid="tab-assign">Assignment</TabsTrigger>
              <TabsTrigger value="close" data-testid="tab-close">Cost & Close</TabsTrigger>
            </TabsList>

            <TabsContent value="request" className="space-y-6 pt-4">
              <Section title="Request & Project">
                <Field label="Request Date" field="request_date" type="date" value={form.request_date} onChange={set} />
                <Field label="Requester Name" field="requester_name" value={form.requester_name} onChange={set} />
                <Field label="Project Name" field="project_name" value={form.project_name} onChange={set} />
                <Field label="Project Number" field="project_number" value={form.project_number} onChange={set} />
                <Field label="Client Name" field="client_name" value={form.client_name} onChange={set} />
                <Field label="Contract Type" field="contract_type" type="select" options={CONTRACT_TYPE} value={form.contract_type} onChange={set} />
              </Section>
              <Section title="Pickup & Delivery">
                <Field label="Pickup Location" field="pickup_location" value={form.pickup_location} onChange={set} />
                <Field label="Pickup Site Contact" field="pickup_site_contact" value={form.pickup_site_contact} onChange={set} />
                <Field label="Pickup Phone" field="pickup_contact_phone" value={form.pickup_contact_phone} onChange={set} />
                <Field label="Delivery Location" field="delivery_location" value={form.delivery_location} onChange={set} />
                <Field label="Delivery Site Contact" field="delivery_site_contact" value={form.delivery_site_contact} onChange={set} />
                <Field label="Delivery Phone" field="delivery_contact_phone" value={form.delivery_contact_phone} onChange={set} />
              </Section>
              <Section title="Dead Leg (Empty Miles)">
                <Field label="Dead Leg Basis" field="dead_leg_basis" type="select" options={["Pickup", "Delivery"]} placeholder="None" value={form.dead_leg_basis} onChange={set} />
                <Field label="Dead Leg Location" field="dead_leg_location" placeholder="Empty from / return to…" value={form.dead_leg_location} onChange={set} />
                <Field label="Dead Leg Miles" field="dead_leg_miles" type="number" value={form.dead_leg_miles} onChange={set} />
                <Field label="Dead Leg Cost ($)" field="dead_leg_cost" type="number" value={form.dead_leg_cost} onChange={set} />
              </Section>
              <Section title="Load Details">
                <Field label="Load Description" field="load_description" type="textarea" span={2} value={form.load_description} onChange={set} />
                <FreightClassField form={form} set={set} />
                <Field label="Special Instructions" field="special_instructions" type="textarea" span={2} value={form.special_instructions} onChange={set} />
                <Field label="Priority" field="priority" type="select" options={PRIORITY} value={form.priority} onChange={set} />
                <Field label="Asset Source" field="asset_source" type="select" options={ASSET_SOURCE} value={form.asset_source} onChange={set} />
                <Field label="Scope of Work" field="scope_of_work" type="textarea" span={2} value={form.scope_of_work} onChange={set} />
              </Section>
              <Section title="Status & Approval">
                <Field label="Status" field="status" type="select" options={STATUS} value={form.status} onChange={set} />
                <Field label="Approved By" field="approved_by" value={form.approved_by} onChange={set} />
                <Field label="Approval Date" field="approval_date" type="date" value={form.approval_date} onChange={set} />
                <div className="sm:col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Approval Document</Label>
                  <div className="mt-1.5">
                    {form.approval_document ? (
                      <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2">
                        <FileCheck className="h-4 w-4 text-emerald-600 shrink-0" />
                        <a href={form.approval_document} target="_blank" rel="noopener noreferrer" download={form.approval_document_name || "approval"}
                          data-testid="view-approval-doc" className="text-sm text-emerald-800 underline truncate flex-1">{form.approval_document_name || "View approval document"}</a>
                        <Button type="button" variant="ghost" size="sm" className="h-7 text-red-600" data-testid="remove-approval-doc"
                          onClick={() => setForm((p) => ({ ...p, approval_document: null, approval_document_name: null }))}>Remove</Button>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" className="gap-2 border-border/60" data-testid="upload-approval-doc-btn"
                        onClick={() => approvalFileRef.current?.click()}><Upload className="h-4 w-4" /> Upload approval document</Button>
                    )}
                    <input ref={approvalFileRef} type="file" accept=".pdf,image/*,.doc,.docx" className="hidden" onChange={onApprovalFile} data-testid="approval-doc-input" />
                    <p className="text-[11px] text-muted-foreground mt-1.5">Attach the signed approval (PDF or image). A job can advance to “Approved” once a document and approver are set.</p>
                  </div>
                </div>
              </Section>
            </TabsContent>

            <TabsContent value="schedule" className="space-y-6 pt-4">
              <Section title="Requested Schedule">
                <Field label="Requested Pickup Date" field="requested_pickup_date" type="date" value={form.requested_pickup_date} onChange={set} />
                <Field label="Requested Delivery Date" field="requested_delivery_date" type="date" value={form.requested_delivery_date} onChange={set} />
              </Section>
              <Section title="Planned & Actual">
                <Field label="Estimated Start" field="estimated_start" type="datetime" value={form.estimated_start} onChange={set} />
                <Field label="Estimated End" field="estimated_end" type="datetime" value={form.estimated_end} onChange={set} />
                <Field label="Estimated Time (hrs)" field="estimated_time_hrs" type="number" value={form.estimated_time_hrs} onChange={set} />
                <Field label="Actual Start" field="actual_start" type="datetime" value={form.actual_start} onChange={set} />
                <Field label="Actual End" field="actual_end" type="datetime" value={form.actual_end} onChange={set} />
                <Field label="Actual Time (hrs)" field="actual_time_hrs" type="number" value={form.actual_time_hrs} onChange={set} />
              </Section>
            </TabsContent>

            <TabsContent value="assign" className="space-y-6 pt-4">
              {isInternal ? (
                <Section title="Internal Asset Assignment">
                  <RefSelect label="Driver" testid="assign-driver" value={form.driver_id} onSelect={selectDriver}
                    options={drivers.map((d) => ({ id: d.id, label: `${d.driver_name}${d.rate_per_hour ? ` — $${d.rate_per_hour}/hr` : ""}` }))} placeholder="Select driver" />
                  <Field label="Employee ID" field="employee_id" value={form.employee_id} onChange={set} />
                  <RefSelect label="Truck" testid="assign-truck" value={form.truck_id} onSelect={selectTruck}
                    options={trucks.map((a) => ({ id: a.id, label: `${a.asset_type} #${a.unit_number} — $${a.rate_per_mile ?? "?"}/mi` }))} placeholder="Select truck" />
                  <RefSelect label="Trailer" testid="assign-trailer" value={form.trailer_id} onSelect={selectTrailer}
                    options={trailers.map((a) => ({ id: a.id, label: `${a.asset_type} #${a.unit_number} — $${a.rate_per_mile ?? "?"}/mi` }))} placeholder="Select trailer (optional)" />
                </Section>
              ) : (
                <>
                  <Section title="Third-Party Assignment">
                    <Field label="Carrier/Vendor" field="third_party_carrier" type="select" options={vendors.map((v) => v.carrier_name).filter(Boolean)} value={form.third_party_carrier} onChange={set} />
                    <Field label="Third-Party Driver" field="third_party_driver" value={form.third_party_driver} onChange={set} />
                    <Field label="Third-Party Contact" field="third_party_contact" value={form.third_party_contact} onChange={set} />
                  </Section>
                  <div className="pt-2">
                    <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0055FF] border-b border-border/60 pb-1.5 mb-3">
                      Vendor RFQ & Quotes
                    </h3>
                    <RfqPanel
                      reqId={editing?.id}
                      rfqs={form.rfqs || []}
                      vendors={vendors}
                      loadInfo={form}
                      onUpdated={(req) => { setForm(req); setEditing(req); invalidate(); }}
                    />
                  </div>
                </>
              )}
            </TabsContent>

            <TabsContent value="close" className="space-y-6 pt-4">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-[#0055FF] border-b border-border/60 pb-1.5 mb-3">
                  Cost Estimator
                </h3>
                <CostEstimator form={form} setForm={setForm} />
              </div>
              <Section title="Cost & Completion">
                <Field label="Estimated Cost ($)" field="estimated_cost" type="number" value={form.estimated_cost} onChange={set} />
                <Field label="Actual Cost ($)" field="actual_cost" type="number" value={form.actual_cost} onChange={set} />
                <Field label="Booked" field="booked" type="select" options={["Yes", "No"]} value={form.booked} onChange={set} />
                <Field label="Closed Date" field="closed_date" type="date" value={form.closed_date} onChange={set} />
                <Field label="Completion Notes" field="completion_notes" type="textarea" span={2} value={form.completion_notes} onChange={set} />
              </Section>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="cancel-req-btn">Cancel</Button>
            <Button data-testid="save-req-btn" className="bg-[#0055FF] hover:bg-[#0044CC]" disabled={saveMut.isPending || checking} onClick={handleSave}>
              {checking ? "Checking…" : saveMut.isPending ? "Saving…" : "Save Requisition"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete requisition?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the record from the Master Log.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-req">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-req" className="bg-red-600 hover:bg-red-700" onClick={() => delMut.mutate(delId)}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!conflicts} onOpenChange={(o) => !o && setConflicts(null)}>
        <AlertDialogContent data-testid="conflict-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="h-5 w-5" /> Scheduling Conflict
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                <p>This assignment overlaps with existing scheduled job(s):</p>
                <ul className="space-y-1.5">
                  {(conflicts || []).map((c) => (
                    <li key={c.id} className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                      <span className="font-mono-data font-semibold text-amber-800">{c.work_order_no || c.requisition_no}</span>
                      {" — "}{c.reasons.join(", ")}
                      <span className="block text-amber-700/80 mt-0.5">
                        {c.estimated_start ? String(c.estimated_start).slice(0, 16).replace("T", " ") : "?"}
                        {c.estimated_end ? ` → ${String(c.estimated_end).slice(0, 16).replace("T", " ")}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-muted-foreground">You can save anyway, or adjust the driver, truck, or schedule.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="conflict-cancel-btn">Adjust</AlertDialogCancel>
            <AlertDialogAction data-testid="conflict-save-anyway-btn" className="bg-amber-600 hover:bg-amber-700"
              onClick={() => { setConflicts(null); saveMut.mutate(form); }}>Save Anyway</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
